import { createContext, useContext, useState, useCallback, FC, ReactNode } from 'react'
import { api } from '../services/apiClient'
import { FirmDetailsContactData, FirmDetailsAddressData, FirmDetailsData } from 'src/dtos/portal'
import ApplicationData from 'src/dtos/applicationData'

interface FirmMainData {
  applicationId: number;
  name: string;
  number: string;
  dateCompleted: string;
  dateSubmitted: string;
  policyExpiryDate?: string;
  today?: string;
}

interface FirmContactsData {
  primaryContact: FirmDetailsContactData;
  secondaryContact: FirmDetailsContactData;
}

interface FirmAddressesData {
  mailingAddress: FirmDetailsAddressData;
  billingAddress: FirmDetailsAddressData;
}

interface CustomerContextData {
  firmData: FirmMainData;
  applicationData: ApplicationData;
  contactsData: FirmContactsData;
  addressesData: FirmAddressesData;
  getFirmDetails: (firmId: number) => Promise<void>;
}

const CustomerContext = createContext<CustomerContextData>({} as CustomerContextData)

export const CustomerProvider: FC<{ children: ReactNode }> = ({children}) => {
  const [firmData, setFirmData] = useState<FirmMainData>({} as FirmMainData)
  const [applicationData, setApplicationData] = useState<ApplicationData>({} as ApplicationData)
  const [contactsData, setContactsData] = useState<FirmContactsData>({} as FirmContactsData)
  const [addressesData, setAddressesData] = useState<FirmAddressesData>({} as FirmAddressesData)

  const getFirmDetails = useCallback(async (firmId: number) => {
    try {
      const [firmDetails, policy] = await Promise.all([
        api.get<FirmDetailsData>(`/Firm/${firmId}`),
        api.get(`/Firm/GetPolicyDetails/${firmId}`)
      ])

      const {
        applicationId,
        name,
        number,
        dateCompleted,
        dateSubmitted,
        primaryContact,
        secondaryContact,
        applicationData,
        mailingAddress,
        billingAddress,
      } = firmDetails?.data

      setFirmData({
        applicationId,
        name,
        number,
        dateCompleted,
        dateSubmitted,
        policyExpiryDate: policy?.data?.policyExpiryDate,
        today: policy?.data?.today
      })
      setApplicationData(applicationData)
      setContactsData({ primaryContact, secondaryContact })
      setAddressesData({ mailingAddress, billingAddress })
    } catch (error) {
      return undefined
    }
  }, [])
  
  return (
    <CustomerContext.Provider
      value={{
        firmData,
        applicationData,
        contactsData,
        addressesData,
        getFirmDetails
      }}
    >
      {children}
    </CustomerContext.Provider>
  )
}

export function useCustomer(): CustomerContextData {
  const context = useContext(CustomerContext)

  if(!context) throw new Error('useCustomer must be used within an AuthProvider!')

  return context
}